import { useAppContext } from "../app/AgTechAppContext";
import { useDataActionExecutor } from "../data/actions/AgTechDataActions";
import { AgTechFormControlIndex, AgTechFormControlMetadata, AgTechFormControlUpdate } from "../forms/AgTechFormControls";
import { useFormDataAction } from "../forms/AgTechFormDataActions";
import { AgTechFormProps } from "../forms/AgTechFormProps";
import { AgTechFormState } from "../forms/AgTechFormState";
import { useAgTechSafeState } from "../utilities/AgTechUtilities";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

export type AgTechFormContext<TInput, TContext = TInput> = {
    entity: TInput,
    context: TContext,
    state: AgTechFormState,
    isDirty: boolean,
    updateFormEntity: <TFormCallbackEntity extends TInput>(formEntityCallback: (currentFormEntity: TFormCallbackEntity) => TFormCallbackEntity) => void,
    handleFieldChanged: <TFormControlDataType, >(value: TFormControlDataType, getUpdatedEntity: AgTechFormControlUpdate<TInput, TFormControlDataType>) => Promise<void>,
    registerFormField: (field: AgTechFormControlMetadata<TInput>) => void,
}

export const FormContext = createContext<AgTechFormContext<any, any>>({
    entity: undefined,
    context: undefined,
    state: AgTechFormState.Open,
    isDirty: false,
    updateFormEntity: () => {
        console.error('Updated form entity in default form context.');
    },
    handleFieldChanged: async (val, getUpdatedEntity) => {
        console.error('Handled field change in default form context.');
        await getUpdatedEntity(undefined, val);
    },
    registerFormField: () => {
        console.error('Registered a form field with the default form context');
    }
});

export const useFormContext = () => useContext(FormContext);

export const useFormContextData = <TFormData, TActionData, TResponseData, TContextData = TFormData>(
    props: AgTechFormProps<TFormData, TActionData, TResponseData, TContextData>
): AgTechFormContext<TFormData, TContextData> => {
    const appContext = useAppContext();
    const dispatch = useDispatch();

    const [formData, updateFormDataRef] = useAgTechSafeState<TFormData>({
        ...props.initialFormData
    });
    const [formContextData, updateFormContextData] = useState<TContextData>(props.contextualData ?? (formData as any));

    const [formState, updateFormState] = useState(AgTechFormState.Open);
    const [isDirty, setIsDirty] = useState(false);

    const formControlsRef = useRef<AgTechFormControlIndex<TFormData>>({});
    const formDataActionExecutor = useDataActionExecutor();

    // Hooks called for realtime form submissions
    const formRealtimeEntityLastUpdateRef = useRef<TFormData>(props.initialFormData);

    const updateFormData = (updatedFormData: TFormData) => {
        if (props.behavior?.eventHandlers?.onFormEntityChanged) {
            props.behavior.eventHandlers.onFormEntityChanged(updatedFormData);
        }
        
        updateFormDataRef(updatedFormData);
    }

    // Wrap the given data action in a wrapping action with form specific business logic
    const formDataAction = useFormDataAction({
        appContext: appContext,
        dispatch: dispatch,
        initialFormData: props.initialFormData,
        getActionData: (data) => props.action.getActionData(data, formContextData),
        formDataRef: formData,
        formControlsRef: formControlsRef,
        formDataAction: props.action.action,
        setFormState: state => {
            updateFormState(state);
            if (state === AgTechFormState.Successful) {
                setIsDirty(false);
            }
        }
    });

    useEffect(() => {
        if (props.behavior?.realtimeSubmissionProps) {
            let hasFormEntityChanged = !props.behavior.realtimeSubmissionProps.isFormEntityTheSame(
                formRealtimeEntityLastUpdateRef.current,
                formData.current);
            
            if (hasFormEntityChanged) {
                formRealtimeEntityLastUpdateRef.current = { ...formData.current };

                formDataActionExecutor.executeAction(formDataAction, {
                    originalEntity: props.action.getActionData(props.initialFormData, formContextData),
                    submittedEntity: props.action.getActionData(formData.current, formContextData)
                });
            }
        }
    }, [formData.current]);

    useEffect(() => {
        updateFormContextData(props.contextualData ?? (formData.current as any));
    }, [props.contextualData]);

    useEffect(() => {
        if (props.structure?.submitButton && props.structure.submitButton.current) {
            props.structure.submitButton.current.click = async () => {       
                await formDataActionExecutor.executeAction(formDataAction, {
                    originalEntity: props.action.getActionData(props.initialFormData, formContextData),
                    submittedEntity: props.action.getActionData(formData.current, formContextData)
                });
            }
        }

        return () => {
            if (props.structure?.submitButton && props.structure.submitButton.current) {
                props.structure.submitButton.current.click = async () => {};
            }
        }
    });
    
    return {
        entity: formData.current,
        context: formContextData,
        state: formState,
        isDirty: isDirty,
        updateFormEntity: function updateEntity<TFormInput extends TFormData>(callback: (currentFormEntity: TFormInput) => TFormInput) {
            let updatedFormEntity = callback(formData.current as TFormInput);
            setIsDirty(true);
            updateFormData({ ...updatedFormEntity });
        },
        handleFieldChanged: async (val, update) => {
            var updatedEntity = await update(formData.current, val);
            if (updatedEntity)
            {
                setIsDirty(true);
                updateFormData({ ...updatedEntity });
            }
        },
        registerFormField: (field: AgTechFormControlMetadata<TFormData>) => {
            formControlsRef.current[field.id] = { ...field };
        }
    }
}